import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, GegAbgleichAntwort, GegAbgleichModell, GegAbgleichTyp } from '../../api';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, formatiereZahl, type Spalte, type Vorbehalt, type Zustand,
} from '../../muster';
import type { Rahmen } from '../Projektrahmen';

/**
 * Der GEG-Abgleich (TL10, Befund TL Z02): je Bauteiltyp des Wärmeschutznachweises, was der Nachweis führt, neben
 * dem, was das Modell daraus macht - Bauteile, Fläche je Randbedingung und der U-Wert, mit dem Heiz- und Kühllast
 * tatsächlich rechnen.
 *
 * ═══ Nur Übernahme und Abgleich ═════════════════════════════════════════════
 *
 * Der GEG-Nachweis ist nicht Leistungsumfang (E5). Die Seite entscheidet nichts und übernimmt nichts in die
 * Rechnung; eine Abweichung ist ein Befund mit Zahl. Der Nachweis führt keine Fläche je Typ - die Flächen
 * stammen aus dem Modell. „Abweichend“ heißt |ΔU| ≥ 0,01 W/(m²K), die Rundung der Nachweisangabe, keine
 * Normgrenze.
 *
 * TX-STANDARD S1: PageHeader mit Stand, Exportleiste (Excel), Vorbehalte, höchstens sechs Kennzahlen, DataTable
 * mit Gruppen je Kategorie, Fachstatus als StatusBadge, Lade-/Leer-/Fehlerzustand.
 */

export function GegAbgleichSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <GegAbgleichAnsicht api={api} projektId={projekt.id} />;
}

/** Die Bewertung im Klartext und als Zustand - dieselben Wörter wie in der Liste (GegAbgleich.Bewertungstext). */
export const BEWERTUNG: Record<string, { text: string; zustand: Zustand }> = {
  gleich: { text: 'gleich', zustand: 'gut' },
  abweichend: { text: 'abweichend', zustand: 'warnung' },
  ohne_modell: { text: 'nicht im Modell', zustand: 'neutral' },
  ohne_nachweiswert: { text: 'ohne Nachweiswert', zustand: 'warnung' },
  nicht_vergleichbar: { text: 'nicht vergleichbar', zustand: 'info' },
};

function bewertung(b: string) {
  return BEWERTUNG[b] ?? { text: b, zustand: 'neutral' as Zustand };
}

const RANG: Record<string, { text: string; zustand: Zustand }> = {
  0: { text: 'Fehler', zustand: 'fehler' },
  1: { text: 'Warnung', zustand: 'warnung' },
  2: { text: 'Hinweis', zustand: 'info' },
};

const TYPSPALTEN: Spalte<GegAbgleichTyp>[] = [
  { schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (t) => t.typCode, inhalt: (t) => <strong>{t.typCode}</strong> },
  { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (t) => t.bezeichnung ?? '' },
  { schluessel: 'status', titel: 'Nachweis', wert: (t) => t.status, versteckt: true },
  { schluessel: 'uvergleich', titel: 'U Nachweis', einheit: 'W/(m²·K)', art: 'zahl', nachkomma: 3, wert: (t) => t.uVergleich,
    titelHinweis: 'U Bilanz, sonst U Anlage 2; beim Erdkontakt U_fg,eff; transparent ohne Bilanzwert U_w/U_cw' },
  { schluessel: 'quelle', titel: 'aus', wert: (t) => t.uVergleichQuelle ?? '', versteckt: true },
  { schluessel: 'urechnung', titel: 'U Rechnung', einheit: 'W/(m²·K)', art: 'zahl', nachkomma: 3, wert: (t) => t.uRechnung,
    titelHinweis: 'flächengewichtet über die Zuordnungen, mit denen Heiz- und Kühllast rechnen' },
  { schluessel: 'delta', titel: 'ΔU', einheit: 'W/(m²·K)', art: 'zahl', nachkomma: 3, wert: (t) => t.deltaU },
  { schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (t) => bewertung(t.bewertung).text,
    inhalt: (t) => <StatusBadge klein zustand={bewertung(t.bewertung).zustand} text={bewertung(t.bewertung).text} /> },
  { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (t) => t.bauteile },
  { schluessel: 'flaeche', titel: 'Fläche wirksam', einheit: 'm²', art: 'zahl', nachkomma: 1, wert: (t) => t.flaecheWirksamM2 },
  { schluessel: 'aussen', titel: 'davon Außenluft', einheit: 'm²', art: 'zahl', nachkomma: 1, wert: (t) => t.flaecheAussenluftM2 },
  { schluessel: 'erdreich', titel: 'davon Erdreich', einheit: 'm²', art: 'zahl', nachkomma: 1, wert: (t) => t.flaecheErdreichM2,
    versteckt: true },
  { schluessel: 'herkunft', titel: 'Herkunft U', wert: (t) => t.uHerkunft ?? '' },
  { schluessel: 'g', titel: 'g', art: 'zahl', nachkomma: 2, wert: (t) => t.gWert, versteckt: true },
];

type Ansatz = GegAbgleichModell['bilanzansaetze'][number];
const ANSATZSPALTEN: Spalte<Ansatz>[] = [
  { schluessel: 'bezeichnung', titel: 'Ansatz', immerSichtbar: true, wert: (a) => a.bezeichnung },
  { schluessel: 'wert', titel: 'Wert', art: 'zahl', nachkomma: 3, wert: (a) => a.zahlwert },
  { schluessel: 'einheit', titel: 'Einheit', wert: (a) => a.einheit ?? '' },
  { schluessel: 'gilt', titel: 'gilt für', wert: (a) => a.giltFuer ?? 'alle' },
  { schluessel: 'bemerkung', titel: 'Bemerkung', wert: (a) => a.bemerkung ?? '' },
];

type Luecke = GegAbgleichModell['ohneTyp'][number];
const LUECKENSPALTEN: Spalte<Luecke>[] = [
  { schluessel: 'art', titel: 'Bauteilart', immerSichtbar: true, wert: (l) => l.bauteilart },
  { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (l) => l.bauteile },
  { schluessel: 'netto', titel: 'Fläche netto', einheit: 'm²', art: 'zahl', nachkomma: 1, wert: (l) => l.flaecheNettoM2 },
  { schluessel: 'aussen', titel: 'gegen außen', einheit: 'm²', art: 'zahl', nachkomma: 1, wert: (l) => l.flaecheGegenAussenM2 },
];

type Befund = GegAbgleichModell['befunde'][number];
const BEFUNDSPALTEN: Spalte<Befund>[] = [
  { schluessel: 'rang', titel: 'Schwere', art: 'status', wert: (b) => RANG[String(b.rang)]?.text ?? String(b.rang),
    inhalt: (b) => <StatusBadge klein zustand={RANG[String(b.rang)]?.zustand ?? 'neutral'}
                                text={RANG[String(b.rang)]?.text ?? String(b.rang)} /> },
  { schluessel: 'regel', titel: 'Regel', wert: (b) => b.regel },
  { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (b) => b.gegenstand },
  { schluessel: 'meldung', titel: 'Meldung', wert: (b) => b.meldung },
];

/** Der Stand im Kopf: Nachweis, Index, Datum. */
export function nachweisText(m: GegAbgleichModell): string {
  const n = m.nachweis;
  const datum = n.stand ? new Date(`${n.stand}T00:00:00Z`).toLocaleDateString('de-DE', { timeZone: 'UTC' }) : null;
  return `Nachweis ${n.kennung} · Index ${n.index}${datum ? ` · ${datum}` : ''}`;
}

export function GegAbgleichAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<GegAbgleichAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const antwort = await api.gegAbgleich(projektId);
      setDaten(antwort ?? null);
      if (!antwort) setFehler('Die Antwort war leer.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const modell = daten?.modell ?? null;

  const kopf = (
    <PageHeader
      titel="GEG-Abgleich"
      beschreibung={'Je Bauteiltyp des Wärmeschutznachweises: was der Nachweis führt und was das Modell daraus macht – '
        + 'Fläche, Randbedingung und der U-Wert der Rechnung. Nur Übernahme und Abgleich, nichts wird entschieden.'}
      meta={modell ? nachweisText(modell) : undefined}
      aktionen={(
        <ExportLeiste ziele={[
          { art: 'excel', laden: () => api.gegAbgleichExcel(projektId),
            gesperrt: modell ? undefined : { grund: 'Kein GEG-Nachweis hinterlegt – ohne Nachweis gibt es keine Liste.' } },
        ]} />
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Der GEG-Abgleich konnte nicht geladen werden." meldung={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Abgleich wird gelesen …" zeilen={8} spalten={6} /></>;

  if (!modell) {
    return (
      <>
        {kopf}
        <EmptyState titel="Kein GEG-Nachweis hinterlegt."
                    erklaerung={daten.hinweis ?? 'Ohne Nachweis gibt es keinen Abgleich.'} />
      </>
    );
  }

  const typen = modell.typen;
  const imModell = typen.filter((t) => t.bauteile > 0);
  const abweichend = typen.filter((t) => t.bewertung === 'abweichend');
  const ohneTyp = modell.ohneTyp.reduce((n, l) => n + l.bauteile, 0);
  const ohneTypAussen = modell.ohneTyp.reduce((n, l) => n + l.flaecheGegenAussenM2, 0);

  const vorbehalte: Vorbehalt[] = [
    { art: 'sonstiges', text: 'Nur Übernahme und Abgleich (E5): Der GEG-Nachweis ist nicht Leistungsumfang, hier wird '
        + 'nichts entschieden und nichts in die Rechnung übernommen.' },
    { art: 'sonstiges', text: 'Der Nachweis führt keine Fläche je Typ – die Flächen stammen aus dem Modell.' },
  ];
  if (abweichend.length > 0) {
    vorbehalte.push({ art: 'fachfrage', text: `${abweichend.length} Typ(en) rechnen mit einem anderen U-Wert als der `
      + 'Nachweis – welcher gilt, entscheidet die Fachplanung.', verweis: 'Befunde' });
  }

  return (
    <>
      {kopf}

      <Vorbehalte eintraege={vorbehalte} />

      <KpiReihe>
        <KpiCard titel="Typen im Nachweis" wert={typen.length} nachkomma={0} />
        <KpiCard titel="im Modell" wert={imModell.length} nachkomma={0} vergleich={`von ${typen.length}`} />
        <KpiCard titel="abweichend" wert={abweichend.length} nachkomma={0}
                 zustand={abweichend.length > 0 ? 'warnung' : undefined}
                 zustandText={abweichend.length > 0 ? `|ΔU| ≥ 0,01 W/(m²K)` : undefined} />
        <KpiCard titel="Bauteile ohne Typ" wert={ohneTyp} nachkomma={0}
                 vergleich={`${formatiereZahl(ohneTypAussen, 0)} m² gegen außen`} />
      </KpiReihe>

      <ContentSection titel="Bauteiltypen" beschreibung={'Nachweis und Modell nebeneinander, gruppiert nach Kategorie. '
        + 'U Rechnung ist flächengewichtet über die Zuordnungen, mit denen Heiz- und Kühllast rechnen.'}>
        <DataTable name="Bauteiltypen" spalten={TYPSPALTEN} zeilen={typen} schluessel={(t) => t.typCode} dicht
                   gruppierung={{ nach: (t) => t.kategorie ?? '(ohne Kategorie)', ordnung: 'eingang' }}
                   leer={<EmptyState klein titel="Der Nachweis führt keine Bauteiltypen." />} />
      </ContentSection>

      <ContentSection titel="Befunde" beschreibung="Ausgewiesen, nicht entschieden.">
        <DataTable name="Befunde" spalten={BEFUNDSPALTEN} zeilen={modell.befunde}
                   schluessel={(b) => `${b.regel}|${b.gegenstand}|${b.meldung}`} dicht
                   leer={<EmptyState klein titel="Keine Befunde." />} />
      </ContentSection>

      <ContentSection titel="Bilanzansätze des Nachweises"
                      beschreibung="Ansätze der DIN-V-18599-Bilanz – sichtbar, nicht in die Rechnung übernommen.">
        <DataTable name="Bilanzansätze" spalten={ANSATZSPALTEN} zeilen={modell.bilanzansaetze}
                   schluessel={(a) => a.schluessel} suche={false} dicht
                   leer={<EmptyState klein titel="Keine Bilanzansätze." />} />
      </ContentSection>

      <ContentSection titel="Bauteile ohne Nachweistyp"
                      beschreibung="„gegen außen“: Zuordnungen an Außenluft, Erdreich, unbeheizter Zone oder angrenzendem Gebäude.">
        <DataTable name="Bauteile ohne Typ" spalten={LUECKENSPALTEN} zeilen={modell.ohneTyp}
                   schluessel={(l) => l.bauteilart} suche={false} dicht
                   leer={<EmptyState klein titel="Jedes Hüllbauteil trägt einen Nachweistyp." />} />
      </ContentSection>

      {modell.hinweise.length > 0 && (
        <ContentSection titel="Hinweise des Nachweises">
          <ul className="liste">
            {modell.hinweise.map((h) => (
              <li key={`${h.typCode}|${h.status}|${h.hinweis}`}>
                <strong>{h.typCode}</strong> ({h.status}): {h.hinweis}
                {h.handlungsempfehlung && <span className="schwach"> – {h.handlungsempfehlung}</span>}
              </li>
            ))}
          </ul>
        </ContentSection>
      )}
    </>
  );
}
